<template>
  <span class="d-inline-block">
    <span ref="trigger" class="d-inline-block" :class="{ 'cursor-pointer': !disabled }" @click.stop="toggle">
      <slot name="button" :open="open" />
    </span>

    <!-- Moved to <body> while open so no scroll container or card can clip it. -->
    <div ref="menu" class="dropdown-menu show floating-menu" :style="style" @click.stop>
      <slot :close="close" />
    </div>
  </span>
</template>

<script>
const GAP = 4;
const MARGIN = 8;

/**
 * A dropdown whose menu is rendered on <body> and positioned from the trigger's
 * on-screen rect, so it is never clipped by (or stretches) the table, card or
 * scroll container it sits in. Opens upward when there is more room above, and
 * scrolls when taller than the space available. Follows the trigger on scroll;
 * closes on outside click, Esc and resize.
 *
 * Slots: `button` (the trigger, receives { open }) and default (the items,
 * receives { close }). Use `.dropdown-item` elements for items.
 */
export default {
  name: 'FloatingMenu',
  props: {
    disabled: { type: Boolean, default: false },
    minWidth: { type: String, default: '10rem' },
  },
  emits: ['show', 'hide'],
  data() {
    return { open: false, menuStyle: {} };
  },
  computed: {
    style() {
      return {
        ...this.menuStyle,
        // Own `display` here rather than v-show, so the two never disagree. The
        // theme collapses .dropdown-menu (transform: scale(1, 0)) unless its
        // direct parent is .show, which a menu moved to <body> never has.
        display: this.open ? 'block' : 'none',
        transform: 'none',
        opacity: 1,
      };
    },
  },
  beforeDestroy() {
    this.close();
    const { menu } = this.$refs;
    if (menu && menu.parentNode) menu.parentNode.removeChild(menu);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      if (this.open) this.close();
      else this.show();
    },
    show() {
      const { menu } = this.$refs;
      if (menu.parentNode !== document.body) document.body.appendChild(menu);
      this.open = true;
      this.$nextTick(this.position);
      document.addEventListener('mousedown', this.onOutside, true);
      document.addEventListener('keydown', this.onKey, true);
      window.addEventListener('scroll', this.onScroll, true);
      window.addEventListener('resize', this.close);
      this.$emit('show');
    },
    close() {
      if (!this.open) return;
      this.open = false;
      document.removeEventListener('mousedown', this.onOutside, true);
      document.removeEventListener('keydown', this.onKey, true);
      window.removeEventListener('scroll', this.onScroll, true);
      window.removeEventListener('resize', this.close);
      this.$emit('hide');
    },
    position() {
      const { trigger, menu } = this.$refs;
      if (!trigger || !menu) return;
      const rect = trigger.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - GAP - MARGIN;
      const above = rect.top - GAP - MARGIN;
      const natural = menu.scrollHeight;
      const up = natural > below && above > below;
      const maxHeight = Math.max(up ? above : below, 120);
      const left = Math.min(rect.left, window.innerWidth - menu.offsetWidth - MARGIN);

      this.menuStyle = {
        position: 'fixed',
        left: `${Math.max(left, MARGIN)}px`,
        top: up ? 'auto' : `${rect.bottom + GAP}px`,
        bottom: up ? `${window.innerHeight - rect.top + GAP}px` : 'auto',
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
        minWidth: this.minWidth,
        zIndex: 1070,
        margin: 0,
      };
    },
    onOutside(event) {
      const { trigger, menu } = this.$refs;
      if (menu.contains(event.target) || trigger.contains(event.target)) return;
      this.close();
    },
    onKey(event) {
      if (event.key === 'Escape') this.close();
    },
    onScroll(event) {
      // Follow the trigger rather than closing: layout scrollbars can emit scroll
      // events at any time, which would otherwise close the menu as it opens.
      const { target } = event;
      // The window itself can be the target, and it is not a Node.
      const insideMenu = target && target.nodeType && this.$refs.menu.contains(target);
      if (!insideMenu) this.position();
    },
  },
};
</script>
